import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { useCallback } from 'react';
import { mutate } from 'swr';
import { useAuth } from 'react-oidc-context';
import { EDGE_API } from '~/lib/constants';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { QuoteAddress } from '~/lib/types/basket-types';
import { savedAddressesBaseKey } from './use-saved-addresses';

export const useSavedAddressPersister = () => {
  const { user, isAuthenticated } = useAuth();
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();

  const saveAddress = useCallback(
    async (address: QuoteAddress, isDefault: boolean) => {
      if (!isAuthenticated || !user) {
        return undefined;
      }

      const response = await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        session,
        `${EDGE_API}/${tenant}/order-process/saved-addresses/saved`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            nickname: address.contactname,
            name: address.contactname,
            email: address.email,
            phone: address.phone,
            organisation: address.organisation,
            property: address.property,
            street: address.street,
            locality: address.locality,
            town: address.town,
            county: address.county,
            postcode: address.postcode,
            country: address.country,
            isDefault: isDefault,
            isBilling: false,
            meta: {
              id: address.metadata?.id ?? undefined,
              type: address.metadata?.type ?? undefined,
              deliveryPointSuffix: address.metadata?.deliveryPointSuffix ?? undefined
            }
          })
        }
      );

      const baseKey = savedAddressesBaseKey(tenant);
      // Invalidate all saved-addresses caches (including search queries) using shared defaults.
      await mutate((key) => typeof key === 'string' && key.startsWith(baseKey), undefined, {
        revalidate: true
      });

      return response;
    },
    [isAuthenticated, user, tenant, settings, session]
  );

  return {
    saveAddress: saveAddress
  };
};
